import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { describeWebAnalyticsError, getWebAnalytics } from '../api/webAnalytics';
import type {
  WebAnalytics,
  WebChannelRow,
  WebDailySeries,
  WebEventRow,
  WebPageRow,
  WebTrafficRow,
} from '../types/webAnalytics';
import styles from './WebAnalytics.module.css';

/**
 * Web Traffic page (Google Analytics 4).
 *
 * Acquisition and behaviour for byok.io, via `/api/admin/web/analytics/`:
 * where visitors come from (channels, source/medium, campaigns), where they
 * land, what they read, on which devices and from which countries, and how
 * each of those converts to GA's purchase key event.
 *
 * Same conventions as the Commerce page: one `days` selector, one network
 * call per window, Recharts area charts, dark theme.
 */

const WINDOWS: ReadonlyArray<{ label: string; days: number }> = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '6m', days: 180 },
  { label: '1y', days: 365 },
];

const TOOLTIP_STYLE = {
  background: '#0d0f14',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 6,
  fontSize: '0.8125rem',
} as const;

function fmtNumber(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return n.toLocaleString();
}

function fmtPct(n: number | null | undefined, digits = 1): string {
  if (n == null || Number.isNaN(n)) return '—';
  return `${n.toFixed(digits)}%`;
}

function fmtDuration(sec: number | null | undefined): string {
  if (sec == null || Number.isNaN(sec)) return '—';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return m > 0 ? `${m}m ${s.toString().padStart(2, '0')}s` : `${s}s`;
}

function formatRelativeTime(date: Date | null): string {
  if (!date) return 'Never';
  const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diffSec < 5) return 'Just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  return `${Math.floor(diffMin / 60)}h ago`;
}

function tickInterval(pointCount: number): number {
  if (pointCount <= 12) return 0;
  return Math.max(Math.floor(pointCount / 12) - 1, 0);
}

/* Charts ---------------------------------------------------------------- */

interface ChartPoint {
  label: string;
  value: number;
}

function seriesToPoints(series: WebDailySeries | undefined): ChartPoint[] {
  if (!series) return [];
  const n = Math.min(series.labels.length, series.data.length);
  const out: ChartPoint[] = [];
  for (let i = 0; i < n; i++) {
    out.push({ label: series.labels[i], value: series.data[i] });
  }
  return out;
}

interface MetricChartProps {
  title: string;
  headlineValue: string;
  subLabel: string;
  color: string;
  series: WebDailySeries | undefined;
  emptyMessage: string;
}

function MetricChart({
  title,
  headlineValue,
  subLabel,
  color,
  series,
  emptyMessage,
}: MetricChartProps) {
  const points = useMemo(() => seriesToPoints(series), [series]);
  const interval = tickInterval(points.length);
  const gradientId = useMemo(
    () => `web-grad-${title.replace(/\s+/g, '-').toLowerCase()}`,
    [title]
  );
  const empty = points.length === 0 || points.every((p) => p.value === 0);

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>{title}</h3>
        <div>
          <span className={styles.sectionTotal}>{headlineValue}</span>
          <span className={styles.sectionSubtotal}>{subLabel}</span>
        </div>
      </div>
      {empty ? (
        <div className={styles.empty}>{emptyMessage}</div>
      ) : (
        <div className={styles.chartWrap}>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.55} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis
                dataKey="label"
                stroke="#9ca3af"
                tick={{ fontSize: 11 }}
                interval={interval}
              />
              <YAxis
                stroke="#9ca3af"
                tick={{ fontSize: 11 }}
                width={56}
                allowDecimals={false}
                tickFormatter={(v) => fmtNumber(typeof v === 'number' ? v : Number(v))}
              />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                labelStyle={{ color: '#e5e7eb' }}
                formatter={(value) => {
                  const n = typeof value === 'number' ? value : Number(value);
                  return [Number.isFinite(n) ? fmtNumber(n) : String(value), title];
                }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={2}
                fill={`url(#${gradientId})`}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

/* Ranked tables --------------------------------------------------------- */

interface Column<Row> {
  key: string;
  header: string;
  render: (row: Row) => string;
  align?: 'left' | 'right';
}

interface RankedTableProps<Row extends { label: string }> {
  title: string;
  subtitle?: string;
  rows: Row[];
  /** Metric used for the inline share bar (relative to the largest row). */
  barValue: (row: Row) => number;
  columns: Column<Row>[];
  emptyMessage: string;
}

function RankedTable<Row extends { label: string }>({
  title,
  subtitle,
  rows,
  barValue,
  columns,
  emptyMessage,
}: RankedTableProps<Row>) {
  const max = rows.reduce((m, r) => Math.max(m, barValue(r)), 0);
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>{title}</h3>
        {subtitle && <span className={styles.sectionSubtotal}>{subtitle}</span>}
      </div>
      {rows.length === 0 ? (
        <div className={styles.empty}>{emptyMessage}</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.thLabel}>&nbsp;</th>
              {columns.map((c) => (
                <th key={c.key} className={c.align === 'left' ? styles.thLeft : styles.thRight}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const share = max > 0 ? (barValue(row) / max) * 100 : 0;
              return (
                <tr key={row.label}>
                  <td className={styles.tdLabel}>
                    <div className={styles.labelText} title={row.label}>
                      {row.label || '(not set)'}
                    </div>
                    <div className={styles.barTrack}>
                      <div className={styles.barFill} style={{ width: `${share}%` }} />
                    </div>
                  </td>
                  {columns.map((c) => (
                    <td key={c.key} className={c.align === 'left' ? styles.tdLeft : styles.tdRight}>
                      {c.render(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

const TRAFFIC_COLUMNS: Column<WebTrafficRow>[] = [
  { key: 'sessions', header: 'Sessions', render: (r) => fmtNumber(r.sessions) },
  { key: 'purchase', header: 'Purchases', render: (r) => fmtNumber(r.purchase) },
  { key: 'rate', header: 'Conv.', render: (r) => fmtPct(r.purchase_rate, 2) },
];

const CHANNEL_COLUMNS: Column<WebChannelRow>[] = [
  { key: 'sessions', header: 'Sessions', render: (r) => fmtNumber(r.sessions) },
  { key: 'users', header: 'Users', render: (r) => fmtNumber(r.totalUsers) },
  { key: 'engaged', header: 'Engaged', render: (r) => fmtPct(r.engagement_rate) },
  { key: 'purchase', header: 'Purchases', render: (r) => fmtNumber(r.purchase) },
  { key: 'rate', header: 'Conv.', render: (r) => fmtPct(r.purchase_rate, 2) },
];

const PAGE_COLUMNS: Column<WebPageRow>[] = [
  { key: 'views', header: 'Views', render: (r) => fmtNumber(r.screenPageViews) },
  { key: 'sessions', header: 'Sessions', render: (r) => fmtNumber(r.sessions) },
];

/* Ecommerce funnel ------------------------------------------------------ */

const FUNNEL_LABELS: Record<string, string> = {
  view_item: 'Viewed product',
  add_to_cart: 'Added to cart',
  begin_checkout: 'Began checkout',
  add_shipping_info: 'Added shipping',
  add_payment_info: 'Added payment',
  purchase: 'Purchased',
};

function EcommerceFunnel({ steps }: { steps: WebEventRow[] }) {
  const top = steps[0]?.count ?? 0;
  if (top === 0) {
    return (
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Ecommerce events</h3>
        </div>
        <div className={styles.empty}>No ecommerce events recorded in this window.</div>
      </div>
    );
  }
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>Ecommerce events</h3>
        <span className={styles.sectionSubtotal}>
          GA standard events · % of product views
        </span>
      </div>
      <div className={styles.funnel}>
        {steps.map((step, i) => {
          const prev = i > 0 ? steps[i - 1].count : null;
          const ofTop = (step.count / top) * 100;
          const ofPrev = prev ? (step.count / prev) * 100 : null;
          return (
            <div key={step.event} className={styles.funnelRow}>
              <div className={styles.funnelLabel}>
                {FUNNEL_LABELS[step.event] ?? step.event}
              </div>
              <div className={styles.funnelBarTrack}>
                <div className={styles.funnelBarFill} style={{ width: `${Math.max(ofTop, 1)}%` }} />
              </div>
              <div className={styles.funnelCount}>{fmtNumber(step.count)}</div>
              <div className={styles.funnelPct}>
                {fmtPct(ofTop)}
                {ofPrev != null && (
                  <span className={styles.funnelStep}> · {fmtPct(ofPrev, 0)} of prev</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* Page ------------------------------------------------------------------ */

export default function WebAnalytics() {
  const [days, setDays] = useState<number>(30);
  const [data, setData] = useState<WebAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const load = useCallback(async (windowDays: number, refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getWebAnalytics(windowDays, refresh);
      setData(result);
      setLastUpdated(new Date());
    } catch (err) {
      setError(describeWebAnalyticsError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(days);
  }, [days, load]);

  const s = data?.summary;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Web Traffic</h1>
          <p className={styles.subtitle}>
            Google Analytics for byok.io: where visitors come from, what they
            look at, and how each source converts to a purchase.
          </p>
        </div>
        <div className={styles.headerActions}>
          <span className={styles.lastUpdated}>Updated {formatRelativeTime(lastUpdated)}</span>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={() => void load(days, true)}
            disabled={loading}
          >
            <span className={`${styles.refreshIcon} ${loading ? styles.spinning : ''}`}>↻</span>
            Refresh
          </button>
        </div>
      </div>

      <div className={styles.windowBar}>
        <span className={styles.windowBarLabel}>Window</span>
        <div className={styles.windowPills}>
          {WINDOWS.map((w) => (
            <button
              key={w.days}
              type="button"
              onClick={() => setDays(w.days)}
              className={`${styles.pill} ${days === w.days ? styles.pillActive : ''}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{fmtNumber(s?.sessions)}</div>
          <div className={styles.statLabel}>Sessions ({days}d)</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{fmtNumber(s?.users)}</div>
          <div className={styles.statLabel}>
            Users · {fmtPct(s?.new_user_share, 0)} new
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{fmtNumber(s?.page_views)}</div>
          <div className={styles.statLabel}>
            Page views · {s ? s.pages_per_session.toFixed(1) : '—'} per session
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{fmtPct(s?.engagement_rate)}</div>
          <div className={styles.statLabel}>
            Engaged sessions · {fmtDuration(s?.avg_session_duration_sec)} avg
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{fmtNumber(s?.purchases)}</div>
          <div className={styles.statLabel}>
            Purchases · {fmtPct(s?.purchase_rate, 2)} of sessions
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{fmtNumber(s?.waitlist_signups)}</div>
          <div className={styles.statLabel}>Waitlist signups</div>
        </div>
      </div>

      {loading && !data ? (
        <div className={styles.loading}>Loading Google Analytics…</div>
      ) : data ? (
        <>
          <div className={styles.chartsGrid}>
            <MetricChart
              title="Daily Sessions"
              headlineValue={fmtNumber(data.daily.sessions.total)}
              subLabel={`${fmtNumber(Math.round(data.daily.sessions.avg_per_day))} / day`}
              color="#3b82f6"
              series={data.daily.sessions}
              emptyMessage="No sessions recorded in this window."
            />
            <MetricChart
              title="Daily New Users"
              headlineValue={fmtNumber(data.daily.new_users.total)}
              subLabel={`${fmtNumber(Math.round(data.daily.new_users.avg_per_day))} / day`}
              color="#a855f7"
              series={data.daily.new_users}
              emptyMessage="No new users recorded in this window."
            />
            <MetricChart
              title="Daily Page Views"
              headlineValue={fmtNumber(data.daily.page_views.total)}
              subLabel={`${fmtNumber(Math.round(data.daily.page_views.avg_per_day))} / day`}
              color="#06b6d4"
              series={data.daily.page_views}
              emptyMessage="No page views recorded in this window."
            />
            <MetricChart
              title="Daily Purchases (GA)"
              headlineValue={fmtNumber(data.daily.purchases.total)}
              subLabel="purchase key events"
              color="#22c55e"
              series={data.daily.purchases}
              emptyMessage="No purchases recorded by GA in this window."
            />
          </div>

          <div className={styles.tablesGrid}>
            <RankedTable
              title="Channels"
              subtitle="default channel group"
              rows={data.channels}
              barValue={(r) => r.sessions}
              columns={CHANNEL_COLUMNS}
              emptyMessage="No channel data."
            />
            <RankedTable
              title="Source / Medium"
              rows={data.sources}
              barValue={(r) => r.sessions}
              columns={TRAFFIC_COLUMNS}
              emptyMessage="No source data."
            />
            <RankedTable
              title="Campaigns"
              subtitle="UTM-tagged traffic only"
              rows={data.campaigns}
              barValue={(r) => r.sessions}
              columns={TRAFFIC_COLUMNS}
              emptyMessage="No tagged campaigns in this window. Add utm_campaign to links in emails and ads to see them here."
            />
            <RankedTable
              title="Landing Pages"
              subtitle="first page of the session"
              rows={data.landing_pages}
              barValue={(r) => r.sessions}
              columns={TRAFFIC_COLUMNS}
              emptyMessage="No landing page data."
            />
            <RankedTable
              title="Top Pages"
              rows={data.pages}
              barValue={(r) => r.screenPageViews}
              columns={PAGE_COLUMNS}
              emptyMessage="No page data."
            />
            <EcommerceFunnel steps={data.ecommerce_funnel} />
            <RankedTable
              title="Devices"
              rows={data.devices}
              barValue={(r) => r.sessions}
              columns={TRAFFIC_COLUMNS}
              emptyMessage="No device data."
            />
            <RankedTable
              title="Countries"
              rows={data.countries}
              barValue={(r) => r.sessions}
              columns={TRAFFIC_COLUMNS}
              emptyMessage="No country data."
            />
          </div>
        </>
      ) : null}

      <div className={styles.notes}>
        <h2 className={styles.notesTitle}>Notes</h2>
        <ul>
          <li>
            Source: GA4 property {data?.property_id ?? '—'} via the Data API.
            Traffic from <code>localhost</code> is excluded.
          </li>
          <li>
            <strong>GA undercounts.</strong> Ad blockers and cookie consent
            mean GA sees fewer visits and purchases than Shopify records. Use
            this page for <em>where traffic comes from</em>; use Shopify
            Analytics for the real order and revenue totals.
          </li>
          <li>
            GA takes up to 24–48 hours to finish processing, so the last day
            or two of every chart will still rise.
          </li>
          <li>
            <strong>Conv.</strong> is GA purchase events divided by sessions
            for that row. Figures are cached server-side for ~30 minutes per
            window; use <strong>Refresh</strong> to pull live data.
          </li>
        </ul>
      </div>
    </div>
  );
}
